<template>
  <div class="add-bank-fee">
    <div class="loader" v-if="state.loading">
      <span aria-hidden="true" class="spinner-border"></span>
    </div>
    <template v-else>
      <div class="profile border-top">
        <div class="profile__left">
          <div class="profile__title">Bank</div>
          <div class="profile__caption">Select the bank to assign a fee</div>
        </div>
        <div class="profile__right">
          <Dropdown
            title="Bank"
            :label="state.form.bank_code"
            :options="bankOptions"
            :searchable="true"
            :onOptionSelected="onBankSelected"
          />
          <span class="attention" v-if="state.errors.bank_code"
            >Field can't be empty</span
          >
        </div>
      </div>

      <div class="profile">
        <div class="profile__left">
          <div class="profile__title">Transaction Type</div>
          <div class="profile__caption">
            This will apply to transactions in the selected module
          </div>
        </div>
        <div class="profile__right">
          <Dropdown
            title="Transaction Type"
            :label="state.form.transaction_type"
            :options="transactionTypes"
            :searchable="true"
            :onOptionSelected="onTransactionTypeSelected"
          />
          <span class="attention" v-if="state.errors.transaction_type"
            >Field can't be empty</span
          >
        </div>
      </div>

      <div class="profile">
        <div class="profile__left">
          <div class="profile__title">Fee</div>
          <div class="profile__caption">Set the fee type and value</div>
        </div>
        <div class="profile__right">
          <div class="form__flex">
            <div class="form__flex__item">
              <label class="form__label">Fee Type</label>
              <div class="select">
                <select class="select__input" v-model="state.form.fee_type">
                  <option value="" disabled>Select Fee Type</option>
                  <option value="FLAT">Flat</option>
                  <option value="PERCENTAGE">Percentage</option>
                </select>
              </div>
              <span class="attention" v-if="state.errors.fee_type"
                >Field can't be empty</span
              >
            </div>
            <div class="form__flex__item add-bank-fee__field--compact">
              <label class="form__label">Fee Value</label>
              <input
                class="form__input"
                type="number"
                min="0"
                step="any"
                v-model.number="state.form.fee_value"
              />
              <span class="attention" v-if="state.errors.fee_value"
                >Field can't be empty</span
              >
            </div>
            <div
              class="form__flex__item add-bank-fee__field--compact"
              v-if="state.form.fee_type === 'PERCENTAGE'"
            >
              <label class="form__label">Fee Cap</label>
              <input
                class="form__input"
                type="number"
                min="0"
                step="any"
                v-model.number="state.form.fee_cap"
              />
              <span class="attention" v-if="state.errors.fee_cap"
                >Field can't be empty</span
              >
            </div>
          </div>
        </div>
      </div>

      <div class="action">
        <button
          :disabled="state.submitting"
          @click="submitFee"
          class="btn btn--primary"
        >
          Save Changes
        </button>
      </div>
    </template>
  </div>
</template>
<script>
import Vue from "vue";
import Dropdown from "@/components/dropdown";
import { reactive, computed, watch } from "@vue/composition-api";

const BANK_FEE_EDIT_KEY = "bankFeeEdit";

const TRANSACTION_TYPE_OPTIONS = [
  "TRANSFER",
  "DIRECT_DEBIT",
  "PAYOUT",
  "BILLS_PAYMENT",
  "CARD",
];

const formatTransactionType = (type) =>
  type
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const transactionTypes = TRANSACTION_TYPE_OPTIONS.map((type) => ({
  id: type,
  value: formatTransactionType(type),
}));

const emptyErrors = () => ({
  bank_code: false,
  transaction_type: false,
  fee_type: false,
  fee_value: false,
  fee_cap: false,
});

const isFormValid = (form) => {
  const base =
    form.bank_code &&
    form.transaction_type &&
    form.fee_type &&
    form.fee_value !== "" &&
    form.fee_value !== null;
  if (form.fee_type === "PERCENTAGE") {
    return base && form.fee_cap !== "" && form.fee_cap !== null;
  }
  return base;
};

const buildPayload = (form) => {
  const payload = {
    bank_code: form.bank_code,
    transaction_type: form.transaction_type,
    fee_type: form.fee_type,
    fee_value: Math.abs(Number(form.fee_value)),
  };
  if (form.fee_type === "PERCENTAGE") {
    payload.fee_cap = Math.abs(Number(form.fee_cap));
  }
  return payload;
};

const parseRawFees = (body) => {
  const data = body.data || body;
  const list = Array.isArray(data) ? data : [data];
  return list.filter(Boolean);
};

const getFeeId = (fee) => fee.id || fee._id || fee.bank_fee_id;

const findFeeById = (fees, id) =>
  fees.find((fee) => String(getFeeId(fee)) === String(id));

const readStoredFee = () => {
  const stored = sessionStorage.getItem(BANK_FEE_EDIT_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch (err) {
    return null;
  }
};

const clearStoredFee = () => sessionStorage.removeItem(BANK_FEE_EDIT_KEY);

const notify = (type, text) => {
  Vue.notify({
    group: "notifications-default",
    type: `${type} text-white`,
    title: type === "success" ? "Success" : "Error",
    text,
  });
};

const getErrorMessage = (err, fallback) =>
  (err.body && err.body.message) || fallback;

const postBankFee = (form) =>
  Vue.http.post("admin/bank-fees", buildPayload(form));

const patchBankFee = (id, form) =>
  Vue.http.patch(`admin/bank-fees/${id}`, buildPayload(form));

const fetchBankFee = async (id) => {
  try {
    const response = await Vue.http.get(`admin/bank-fees/${id}`);
    return response.body.data || response.body;
  } catch (err) {
    const response = await Vue.http.get("admin/bank-fees");
    return findFeeById(parseRawFees(response.body), id);
  }
};

export default {
  metaInfo() {
    const isEdit = this.$route.name === "settings-fee-edit";
    return {
      title: isEdit ? "Settings - Edit Bank Fee" : "Settings - Add Bank Fee",
    };
  },
  components: { Dropdown },
  setup(props, { root }) {
    const store = root.$store;
    const isEdit = computed(() => root.$route.name === "settings-fee-edit");
    const isEditRoute = () => root.$route.name === "settings-fee-edit";

    const state = reactive({
      form: {
        bank_code: "",
        transaction_type: "",
        fee_type: "",
        fee_value: "",
        fee_cap: "",
      },
      feeId: null,
      errors: emptyErrors(),
      submitting: false,
      loading: isEditRoute(),
    });

    const bankOptions = computed(() =>
      (store.state.bankList || []).map((bank) => ({
        id: bank.code,
        value: bank.bank || bank.name || bank.code,
      }))
    );
    const prefillForm = (fee) => {
      const feeId = getFeeId(fee);
      state.feeId = feeId;
      state.form.bank_code = fee.bank_code || fee["BANK CODE"] || "";
      state.form.transaction_type =
        fee.transaction_type || fee["TRANSACTION TYPE"] || "";
      state.form.fee_type = fee.fee_type || fee["FEE TYPE"] || "";
      state.form.fee_value =
        fee.fee_value !== undefined && fee.fee_value !== null
          ? fee.fee_value
          : fee["FEE VALUE"] || "";
      state.form.fee_cap =
        fee.fee_cap !== undefined && fee.fee_cap !== null
          ? fee.fee_cap
          : fee["FEE CAP"] || "";
    };

    const loadFeeForEdit = async (id) => {
      state.loading = true;
      const storedFee = readStoredFee();
      try {
        const fee = (await fetchBankFee(id)) || storedFee;
        if (!fee) return notify("error", "Bank fee not found");
        prefillForm(fee);
        clearStoredFee();
      } catch (err) {
        if (storedFee) {
          prefillForm(storedFee);
          clearStoredFee();
          return;
        }
        notify("error", getErrorMessage(err, "Failed to load bank fee"));
      } finally {
        state.loading = false;
      }
    };

    const onBankSelected = ({ id }) => {
      state.form.bank_code = id;
    };

    const onTransactionTypeSelected = ({ id }) => {
      state.form.transaction_type = id;
    };

    const validateForm = () => {
      const isPercentage = state.form.fee_type === "PERCENTAGE";
      state.errors = {
        bank_code: !state.form.bank_code,
        transaction_type: !state.form.transaction_type,
        fee_type: !state.form.fee_type,
        fee_value:
          state.form.fee_value === "" || state.form.fee_value === null,
        fee_cap:
          isPercentage &&
          (state.form.fee_cap === "" || state.form.fee_cap === null),
      };
      return isFormValid(state.form);
    };

    const submitFee = async () => {
      if (!validateForm()) return;
      state.submitting = true;
      try {
        if (isEdit.value) {
          await patchBankFee(state.feeId, state.form);
          notify("success", "Bank fee updated successfully");
        } else {
          await postBankFee(state.form);
          notify("success", "Bank fee created successfully");
        }
        root.$router.push({ name: "settings-fee" });
      } catch (err) {
        const action = isEdit.value ? "update" : "create";
        notify("error", getErrorMessage(err, `Failed to ${action} bank fee`));
      }
      state.submitting = false;
    };

    watch(
      () => state.form.fee_type,
      (type) => {
        if (type !== "PERCENTAGE") state.form.fee_cap = "";
      }
    );

    watch(
      () => root.$route.params.id,
      (id) => {
        if (isEditRoute() && id) loadFeeForEdit(id);
      },
      { immediate: true }
    );

    return {
      isEdit,
      state,
      bankOptions,
      transactionTypes,
      onBankSelected,
      onTransactionTypeSelected,
      submitFee,
    };
  },
};
</script>
<style lang="scss" scoped>
.add-bank-fee__field--compact {
  flex: 0 0 160px;
  width: 160px;
}
</style>
